import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { getNewsStory } from "../../actions/ApiCalls";
import { IMAGE_URL } from "../../path";
import "../Story.css";

interface NewsStoryProps {
    slug: string;
}

function NewsStory2({ slug }: NewsStoryProps) {
    const [article, setArticle] = useState<any>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [body, setBody] = useState<string[]>([]);
    const [rightParagraphs, setRightParagraphs] = useState<string[]>([]);
    const [remainingParagraphs, setRemainingParagraphs] = useState<string[]>([]);
    const leftRef = useRef<HTMLDivElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const isMobile = windowWidth <= 835;
    const hasQuote = !!article?.content_2;


    const globalStyles = `
        a {
            color: black !important;
            text-decoration: underline;
        }
        .article-body p {
            font-size: 13px !important;
            line-height: 16.9px;
            font-weight: 400;
            margin: 0 !important;
            padding: 0 !important;
        }
        strong {
            font-weight: 600;
        }
        .content-2-style * {
            font-size: 20px !important;
            line-height: 26px;
            font-weight: 300;
        }
        .content-2-style {
            font-size: 20px !important;
            width: 80%;
            margin-top: 0px !important;
            padding-top: 0 !important;
            margin-right: 20px;
            max-height: 120px;
        }
        .content-2-style p:first-child {
            margin-top: 0 !important;
        }
        @media (max-width: 835px) {
            .content-2-style {
                float: none !important;
                width: 100% !important;
                margin-right: 0 !important;
                text-align: left;
            }
        }
    `;

    // Handle window resize
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch article
    useEffect(() => {
        window.scrollTo(0, 0);
        if (slug) {
            getNewsStory(slug)
                .then((response) => {
                    document.title = "Vinimark | " + response.data?.title;
                    setArticle(response.data);
                    splitBody(response.data.content);
                })
                .catch((error) => console.error("Failed to fetch news story:", error));
        }
    }, [slug]);

    // Only split paragraphs if quote exists and desktop
    useEffect(() => {
        if (!isMobile && hasQuote && article) splitRightParagraphs();
    }, [body, article, windowWidth, isMobile, hasQuote]);

    function splitBody(content: string) {
        const articles = content.split("</p>");
        const fullArticles = articles.map((article) => (article ? article + "</p>" : article));
        setBody(fullArticles);
    }

    function splitRightParagraphs() {
        if (!leftRef.current || !rightRef.current) return;

        const leftBottom = leftRef.current.getBoundingClientRect().bottom;
        const rightTop = rightRef.current.getBoundingClientRect().top;

        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.visibility = "hidden";
        tempDiv.style.width = rightRef.current.offsetWidth + "px";
        tempDiv.style.fontWeight = "400";
        tempDiv.style.fontSize = "16px";
        tempDiv.style.lineHeight = "22px";
        document.body.appendChild(tempDiv);

        let accumulatedTop = rightTop;
        const right: string[] = [];
        const remaining: string[] = [];

        for (let para of body) {
            tempDiv.innerHTML = para;
            const paraHeight = tempDiv.offsetHeight;

            if (accumulatedTop < leftBottom) {
                right.push(para);
                accumulatedTop += paraHeight;
            } else {
                remaining.push(para);
            }
        }

        document.body.removeChild(tempDiv);
        setRightParagraphs(right);
        setRemainingParagraphs(remaining);
    }

    const linkStyle: React.CSSProperties = {
        color: "black",
        fontSize: "10px",
        fontFamily: "'Vinimark-Diatype', sans-serif",
        fontWeight: 600,
        textTransform: "uppercase",
        textDecoration: "underline",
        cursor: "pointer",
    };

    const bodyImagesWithPortrait = article
        ? Object.keys(article)
            .filter((key) => key.startsWith("image_path_body_") && article[key])
            .sort((a, b) => parseInt(a.split("_").pop() || "0") - parseInt(b.split("_").pop() || "0"))
            .map((key) => {
                const index = key.split("_").pop();
                const portraitKey = `image_${index}_portrait`;
                return {
                    path: article[key],
                    isPortrait: article[portraitKey] === 1,
                };
            })
        : [];

    const chunkArray = (arr: any[], sizes: number[]) => {
        const chunks = [];
        let index = 0;
        for (let size of sizes) {
            if (index >= arr.length) break;
            chunks.push(arr.slice(index, index + size));
            index += size;
        }
        if (index < arr.length) chunks.push(arr.slice(index));
        return chunks;
    };

    const layoutSizes = isMobile ? [2, 2, 2, 1] : [4, 2, 1];
    const imageChunks = chunkArray(bodyImagesWithPortrait, layoutSizes);

    // Desktop content renderer
    function getDesktopContent() {
        if (!article) return null;

        if (!hasQuote) {
            // No content_2: render full width left-aligned
            return (
                <div style={{ textAlign: "left", width: "100%" }}>
                    <p style={{ fontWeight: 600, marginTop: 0, textTransform: "uppercase" }}>{article?.title}</p>
                    <p className="date-class">{article?.year_posted}</p>
                    <div className="article-body" dangerouslySetInnerHTML={{ __html: article?.content }} />
                </div>
            );
        }

        // content_2 exists: render left/right split
        return (
            <>
                <div style={{ display: "flex", gap: "24px" }}>
                    {/* Left quote/content_2 */}
                    <div style={{ flex: 1, paddingRight: "24px" }}>
                        <div
                            className="content-2-style"
                            ref={leftRef}
                            dangerouslySetInnerHTML={{ __html: article.content_2 }}
                            style={{
                                width: "70%",
                                textAlign: "right",
                                marginLeft: "auto",
                            }}
                        />
                    </div>

                    {/* Right main body */}
                    <div ref={rightRef} style={{ flex: 1 }}>
                        <p style={{ fontWeight: 600, marginTop: 0, textTransform: "uppercase" }}>{article?.title}</p>
                        <p className="date-class">{article?.year_posted}</p>
                        <div className="article-body">
                            {rightParagraphs.map((para, idx) => (
                                <div key={idx} dangerouslySetInnerHTML={{ __html: para }} />
                            ))}
                        </div>
                    </div>
                </div>

                {remainingParagraphs.length > 0 && (
                    <div style={{ marginTop: "24px" }} className="article-body">
                        {remainingParagraphs.map((para, idx) => (
                            <div key={idx} dangerouslySetInnerHTML={{ __html: para }} />
                        ))}
                    </div>
                )}
            </>
        );
    }

    return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative", width: "100%" }}>
            <style>{globalStyles}</style>
            <Header />

            {/* Back Link */}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", marginTop: "140px" }}>
                <div style={{ flex: 1 }} />
                <div style={{ flex: 1, textAlign: "left" }}>
                    <p style={{ fontWeight: 400, marginLeft: "26px" }}>
                        <a style={linkStyle} onClick={() => navigate("/news")}>
                            BACK to stories
                        </a>
                    </p>
                </div>
            </div>

            {/* Image / Video Section */}
            <div style={{ marginTop: "100px", display: "flex", justifyContent: "center" }}>
                <div style={{ width: isMobile ? "100%" : "70%" }}>
                    {article?.video_iframe ? (
                        <iframe
                            src={article.video_iframe}
                            style={{ width: "100%", aspectRatio: "16/9", border: "none" }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                            title={article?.title || "video"}
                        />
                    ) : (
                        <img
                            style={{ width: "100%", display: "block", objectFit: "cover" }}
                            src={IMAGE_URL + article?.image_path}
                            alt={article?.title}
                        />
                    )}
                </div>
            </div>

            {/* Article Content */}
            <div style={{ marginTop: "60px", display: "flex", justifyContent: "center" }}>
                <div style={{ width: isMobile ? "90%" : "70%", position: "relative", textAlign: "left" }}>
                    {isMobile ? (
                        <>
                            {article?.content_2 && (
                                <div
                                    className="content-2-style"
                                    style={{ marginTop: "20px", textAlign: "left" }}
                                    dangerouslySetInnerHTML={{ __html: article.content_2 }}
                                />
                            )}
                            <p style={{ fontWeight: 600, marginTop: 0, textTransform: "uppercase" }}>{article?.title}</p>
                            <p className="date-class">{article?.year_posted}</p>
                            <div className="article-body" dangerouslySetInnerHTML={{ __html: article?.content }} />
                        </>
                    ) : (
                        getDesktopContent()
                    )}
                </div>
            </div>

            {/* Body Images Section */}
            <div style={{ padding: "10px" }}>
                {imageChunks.map((chunk, idx) => (
                    <div
                        key={idx}
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "24px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            marginTop: idx === 0 ? "30px" : "24px",
                        }}
                    >
                        {chunk.map((imageData: any, imgIdx: number) => {
                            const isOnlyHorizontalInRow = chunk.length === 1 && !imageData.isPortrait;
                            return (
                                <img
                                    key={imgIdx}
                                    src={IMAGE_URL + imageData.path}
                                    alt={`body-img-${imgIdx}`}
                                    style={{
                                        width: isOnlyHorizontalInRow
                                            ? isMobile
                                                ? "100%"
                                                : "99%"
                                            : isMobile
                                                ? imageData.isPortrait
                                                    ? "46.7%"
                                                    : "100%"
                                                : imageData.isPortrait
                                                    ? "23.5%"
                                                    : "48.5%",
                                        aspectRatio: imageData.isPortrait ? "4/5" : "16/9",
                                        maxWidth: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>

            <Footer />
        </div>
    );
}

export default NewsStory2;
